import type { AxiosResponse } from 'axios';
import { createApiClient } from './apiClient';

const scenicAdmissionClient = createApiClient('/scenic/api');

export type PolicyTicketState = 'ACTIVE' | 'FROZEN' | 'CLOSED' | 'NONE';
export type AdmissionOperationStatus =
  | 'RECEIVED'
  | 'AUTHORIZED'
  | 'SUBMITTED'
  | 'UNKNOWN'
  | 'RECONCILING'
  | 'CONFIRMED'
  | 'REJECTED'
  | 'REVERTED';

export interface PolicyTicket {
  tokenId: string;
  slotId: string;
  issuerAddress: string;
  ownerAddress: string;
  policyId: string;
  policyVersion: number;
  policyName?: string | null;
  consumedUnits: string;
  admissionNonce: string;
  state: PolicyTicketState;
  createdAtMs: number;
  hasLifetimeLimit?: boolean | null;
  lifetimeLimit?: string | null;
  remainingUnits?: string | null;
  maxUnitsPerAdmission?: string | null;
}

export interface PolicyTicketPage {
  items: PolicyTicket[];
  total: string;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface TicketPresentation {
  presentationId: string;
  tokenId: string;
  requestedUnits: number;
  challenge: string;
  expiresAt: string;
  expiresAtMs: number;
  status: string;
  verifiedSubjectCount: number;
}

export interface AdmissionPreflight {
  allowed: boolean;
  reasonCode: string;
  decisionHash: string;
  tokenId: string;
  chainPolicyId: string;
  policyVersion: number;
  expectedNonce: string;
  consumedUnits: string;
  evidence: Record<string, unknown> | string;
}

export interface AdmissionOperation {
  operationId: string;
  status: AdmissionOperationStatus;
  confirmationPending: boolean;
  presentationId: string;
  tokenId: string;
  consumeUnits: number;
  decisionHash?: string | null;
  chainTxHash?: string | null;
  chainBlockNumber?: string | null;
  chainLogIndex?: number | null;
  resultNonce?: string | null;
  totalConsumedUnits?: string | null;
  failureCode?: string | null;
  failureDetail?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TicketPolicySummary {
  id: number;
  issuerAddress: string;
  policyKey: string;
  chainPolicyId: string;
  name: string;
  status: 'ACTIVE' | 'RETIRED';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyModuleOption {
  key: string;
  name: string;
  description: string;
  address: string;
  moduleId: string;
  version: number;
  supportedFlags: number;
  capabilities: string[];
  active: boolean;
}

export interface TicketPolicyVersion {
  id: number;
  policyId: number;
  version: number;
  status: 'DRAFT' | 'PUBLISHED' | 'SUSPENDED' | 'RETIRED';
  schemaVersion: string;
  evaluatorVersion: string;
  policyDocument: Record<string, unknown>;
  configHash: string;
  moduleAddress: string;
  validFromMs: number;
  validUntilMs: number;
  hasLifetimeLimit: boolean;
  lifetimeLimit?: number | null;
  maxUnitsPerAdmission: number;
  flags: number;
  publishTransactionHash?: string | null;
  createdAt: string;
  publishedAt?: string | null;
  updatedAt: string;
}

export type TicketProductMode = 'LEGACY' | 'POLICY_BOUND';
export type TicketProductAudience = 'INDIVIDUAL' | 'GROUP' | 'FAMILY';

export interface TicketProductTemplate {
  key: string;
  version: number;
  name: string;
  description: string;
  recommendedAudience: TicketProductAudience;
  policyRequired: boolean;
  capabilities: string[];
}

export interface TicketProduct {
  id: number;
  issuerAddress: string;
  scenicAreaId: number;
  productCode: string;
  displayName: string;
  mode: TicketProductMode;
  audienceType: TicketProductAudience;
  templateKey: string;
  templateVersion: number;
  category: string;
  location: string;
  description: string;
  totalSupply: number;
  minPriceRaw: string;
  maxPriceRaw: string;
  validFromMs: number;
  validUntilMs: number;
  policyVersionId?: number | null;
  chainSlotId?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTicketProductInput {
  productCode: string;
  displayName: string;
  mode: TicketProductMode;
  audienceType: TicketProductAudience;
  templateKey: string;
  templateVersion: number;
  category: string;
  location: string;
  description: string;
  totalSupply: number;
  minPriceRaw: string;
  maxPriceRaw: string;
  validFromMs: number;
  validUntilMs: number;
  policyVersionId?: number;
}

export interface TicketProductIssuance {
  id: number;
  productId: number;
  state: 'PREPARED' | 'SLOT_SUBMITTING' | 'SLOT_CREATED' | 'BINDING_SUBMITTING'
    | 'POLICY_BOUND' | 'MINTING' | 'COMPLETED' | 'RECONCILING' | 'FAILED';
  chainSlotId?: string | null;
  slotTxHash?: string | null;
  bindingTxHash?: string | null;
  mintTxHash?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  updatedAt: string;
}

export interface PolicySimulationResult {
  allowed: boolean;
  reasonCode: string;
  decisionHash: string;
  evidence: Record<string, unknown>;
}

export interface TicketGroupSubject {
  id: number;
  holderAddress: string;
  groupRef: string;
  subjectRef: string;
  status: 'ACTIVE' | 'REVOKED';
  validFrom: string;
  validUntil: string;
}

export interface GateDevice {
  deviceId: string;
  issuerAddress: string;
  gateId: string;
  displayName: string;
  status: 'ACTIVE' | 'REVOKED';
  lastSeenAt?: string | null;
  createdAt?: string | null;
}

export interface GateDeviceCredential {
  deviceId: string;
  gateId: string;
  displayName: string;
  credential: string;
}

export interface GateDeviceEnrollment {
  device: GateDevice;
  credential: string;
}

export interface AdmissionCalendarDay {
  id: number;
  calendarRef: string;
  serviceDate: string;
  status: 'OPEN' | 'CLOSED';
  capacityUnits: number;
  reservedUnits: number;
}

export interface TicketReservation {
  reservationRef: string;
  tokenId: string;
  calendarRef: string;
  serviceDate: string;
  reservedUnits: number;
  status: 'ACTIVE' | 'PRESENTED' | 'USED' | 'CANCELLED' | 'EXPIRED';
  expiresAt: string;
}

export interface GateChallengePayload {
  kind: 'kxdex-admission-gate';
  version: 2;
  gateId: string;
  challenge: string;
  expiresAt: number;
}

export interface TicketPresentationPayload {
  kind: 'kxdex-ticket-presentation';
  version: 2;
  presentationId: string;
  challenge: string;
  gateId?: string;
  expiresAt: number;
}

const unwrap = <T>(response: AxiosResponse<T>) => response.data;
const gateDeviceStorageKey = 'kxdex_gate_device_credential';

export const getGateDeviceCredential = (): GateDeviceCredential | null => {
  try {
    const raw = sessionStorage.getItem(gateDeviceStorageKey);
    if (!raw) return null;
    const value = JSON.parse(raw) as GateDeviceCredential;
    return value.deviceId && value.gateId && value.credential ? value : null;
  } catch {
    return null;
  }
};

export const setGateDeviceCredential = (value: GateDeviceCredential | null) => {
  if (value) sessionStorage.setItem(gateDeviceStorageKey, JSON.stringify(value));
  else sessionStorage.removeItem(gateDeviceStorageKey);
};

const gateDeviceHeaders = () => {
  const device = getGateDeviceCredential();
  if (!device) throw new Error('当前会话未绑定受信闸机设备');
  return {
    'X-Kxdex-Gate-Device-Id': device.deviceId,
    'X-Kxdex-Gate-Device-Credential': device.credential,
  };
};

export const policyAdmissionService = {
  async enrollGateDevice(input: { gateId: string; displayName: string }) {
    return unwrap(await scenicAdmissionClient.post<GateDeviceEnrollment>('/v2/gate-devices', input));
  },
  async listGateDevices() {
    return unwrap(await scenicAdmissionClient.get<GateDevice[]>('/v2/gate-devices'));
  },
  async revokeGateDevice(deviceId: string) {
    return unwrap(await scenicAdmissionClient.delete<GateDevice>(
      `/v2/gate-devices/${encodeURIComponent(deviceId)}`,
    ));
  },
  async listMyTickets(offset = 0, limit = 20) {
    return unwrap(await scenicAdmissionClient.get<PolicyTicketPage>('/v2/policy-tickets', {
      params: { offset, limit },
    }));
  },
  async issuePresentation(input: {
    tokenId: string;
    requestedUnits: number;
    gateId?: string;
    gateChallenge?: string;
    groupRef?: string;
    subjectRefs?: string[];
    reservationRef?: string;
  }) {
    return unwrap(await scenicAdmissionClient.post<TicketPresentation>('/v2/ticket-presentations', input));
  },
  async listMyReservations() {
    return unwrap(await scenicAdmissionClient.get<TicketReservation[]>('/v2/ticket-reservations'));
  },
  async createReservation(input: {
    tokenId: string;
    calendarRef: string;
    serviceDate: string;
    reservedUnits: number;
  }) {
    return unwrap(await scenicAdmissionClient.post<TicketReservation>('/v2/ticket-reservations', input));
  },
  async listAdmissionCalendars(from: string, to: string) {
    return unwrap(await scenicAdmissionClient.get<AdmissionCalendarDay[]>('/v2/admission-calendars', {
      params: { from, to },
    }));
  },
  async upsertAdmissionCalendar(input: {
    calendarRef: string;
    serviceDate: string;
    status: 'OPEN' | 'CLOSED';
    capacityUnits: number;
  }) {
    return unwrap(await scenicAdmissionClient.put<AdmissionCalendarDay>('/v2/admission-calendars', input));
  },
  async listMyGroupSubjects(tokenId: string) {
    return unwrap(await scenicAdmissionClient.get<TicketGroupSubject[]>(
      `/v2/policy-tickets/${encodeURIComponent(tokenId)}/group-subjects`,
    ));
  },
  async provisionGroupSubject(input: {
    holderAddress: string;
    groupRef: string;
    subjectRef: string;
    subjectHash: string;
    validFromMs: number;
    validUntilMs: number;
  }) {
    return unwrap(await scenicAdmissionClient.post<TicketGroupSubject>(
      '/v2/ticket-group-subjects', input,
    ));
  },
  async listIssuedGroupSubjects() {
    return unwrap(await scenicAdmissionClient.get<TicketGroupSubject[]>('/v2/ticket-group-subjects'));
  },
  async revokeGroupSubject(subjectId: number) {
    return unwrap(await scenicAdmissionClient.delete<TicketGroupSubject>(
      `/v2/ticket-group-subjects/${subjectId}`,
    ));
  },
  async preflight(input: { presentationId: string; challenge: string; gateId?: string }) {
    return unwrap(await scenicAdmissionClient.post<AdmissionPreflight>(
      '/v2/admissions/preflight', input, { headers: gateDeviceHeaders() },
    ));
  },
  async admit(
    input: { presentationId: string; challenge: string; gateId?: string },
    operationId: string,
  ) {
    return unwrap(await scenicAdmissionClient.post<AdmissionOperation>('/v2/admissions', input, {
      headers: { 'Idempotency-Key': operationId, ...gateDeviceHeaders() },
    }));
  },
  async getAdmission(operationId: string) {
    return unwrap(await scenicAdmissionClient.get<AdmissionOperation>(
      `/v2/admissions/${encodeURIComponent(operationId)}`,
    ));
  },
  async getAdmissionByPresentation(presentationId: string) {
    const response = await scenicAdmissionClient.get<AdmissionOperation | undefined>(
      `/v2/ticket-presentations/${encodeURIComponent(presentationId)}/admission`,
    );
    return response.status === 204 ? undefined : response.data;
  },
  async reconcileAdmission(operationId: string) {
    return unwrap(await scenicAdmissionClient.post<AdmissionOperation>(
      `/v2/admissions/${encodeURIComponent(operationId)}/reconcile`,
    ));
  },
  async listAdmissionReconciliationQueue() {
    return unwrap(await scenicAdmissionClient.get<AdmissionOperation[]>(
      '/v2/admissions/reconciliation',
    ));
  },
  async listPolicies() {
    return unwrap(await scenicAdmissionClient.get<TicketPolicySummary[]>('/v1/scenic/policies'));
  },
  async listPolicyModules() {
    return unwrap(await scenicAdmissionClient.get<PolicyModuleOption[]>('/v1/scenic/policies/modules'));
  },
  async createPolicy(input: { policyKey: string; name: string }) {
    return unwrap(await scenicAdmissionClient.post<TicketPolicySummary>('/v1/scenic/policies', input));
  },
  async listPolicyVersions(policyId: number) {
    return unwrap(await scenicAdmissionClient.get<TicketPolicyVersion[]>(
      `/v1/scenic/policies/${policyId}/versions`,
    ));
  },
  async createPolicyDraft(policyId: number, definition: Record<string, unknown>) {
    return unwrap(await scenicAdmissionClient.post<TicketPolicyVersion>(
      `/v1/scenic/policies/${policyId}/versions`, definition,
    ));
  },
  async validatePolicyDraft(versionId: number) {
    return unwrap(await scenicAdmissionClient.post<Record<string, unknown>>(
      `/v1/scenic/policies/versions/${versionId}/validate`,
    ));
  },
  async simulatePolicy(versionId: number, input: {
    consumeUnits: number;
    evaluatedAtMs: number;
    consumedUnits: string;
    bucketConsumedUnits: Record<string, string>;
    verifiedSubjectCount?: number;
    reservationProvided: boolean;
    calendarAllowed?: boolean;
    chainPolicyUsable: boolean;
  }) {
    return unwrap(await scenicAdmissionClient.post<PolicySimulationResult>(
      `/v1/scenic/policies/versions/${versionId}/simulate`, input,
    ));
  },
  async publishPolicyDraft(versionId: number) {
    return unwrap(await scenicAdmissionClient.post<TicketPolicyVersion>(
      `/v1/scenic/policies/versions/${versionId}/publish`,
    ));
  },
  async listTicketProductTemplates() {
    return unwrap(await scenicAdmissionClient.get<TicketProductTemplate[]>('/v1/scenic/ticket-products/templates'));
  },
  async listTicketProducts() {
    return unwrap(await scenicAdmissionClient.get<TicketProduct[]>('/v1/scenic/ticket-products'));
  },
  async createTicketProduct(input: CreateTicketProductInput) {
    return unwrap(await scenicAdmissionClient.post<TicketProduct>('/v1/scenic/ticket-products', input));
  },
  async issueTicketProduct(productId: number, idempotencyKey: string) {
    return unwrap(await scenicAdmissionClient.post<TicketProductIssuance>(
      `/v1/scenic/ticket-products/${productId}/issue`, {},
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ));
  },
  async getTicketProductIssuance(productId: number) {
    return unwrap(await scenicAdmissionClient.get<TicketProductIssuance>(
      `/v1/scenic/ticket-products/${productId}/issuance`,
    ));
  },
  async reconcileTicketProductIssuance(productId: number) {
    return unwrap(await scenicAdmissionClient.post<TicketProductIssuance>(
      `/v1/scenic/ticket-products/${productId}/issuance/reconcile`,
    ));
  },
  async uploadTicketProductCover(slotId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return unwrap(await scenicAdmissionClient.post<{ slotId: number; imageUrl: string }>(
      `/v1/scenic/tickets/slots/${slotId}/cover`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ));
  },
};

const randomBase64Url = (byteLength: number) => {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  let binary = '';
  bytes.forEach((value) => { binary += String.fromCharCode(value); });
  return btoa(binary).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
};

export const createGateChallenge = (gateId: string, ttlMs = 120_000): GateChallengePayload => {
  const normalizedGateId = gateId.trim();
  if (!normalizedGateId || normalizedGateId.length > 128) {
    throw new Error('闸机编号长度必须为 1-128 个字符');
  }
  return {
    kind: 'kxdex-admission-gate',
    version: 2,
    gateId: normalizedGateId,
    challenge: randomBase64Url(24),
    expiresAt: Date.now() + ttlMs,
  };
};

export const encodeAdmissionPayload = (
  payload: GateChallengePayload | TicketPresentationPayload,
) => JSON.stringify(payload);

const parsePayload = (raw: string): Record<string, unknown> => {
  const value = raw.trim();
  if (!value) throw new Error('票码内容不能为空');
  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    throw new Error('票码不是有效的 RTAX JSON 数据');
  }
};

export const parseGateChallengePayload = (raw: string): GateChallengePayload => {
  const value = parsePayload(raw);
  if (value.kind !== 'kxdex-admission-gate' || value.version !== 2
      || typeof value.gateId !== 'string' || typeof value.challenge !== 'string'
      || typeof value.expiresAt !== 'number') {
    throw new Error('无法识别的闸机挑战码');
  }
  if (value.expiresAt <= Date.now()) throw new Error('闸机挑战码已过期，请重新扫码');
  return value as unknown as GateChallengePayload;
};

export const parseTicketPresentationPayload = (raw: string): TicketPresentationPayload => {
  const value = parsePayload(raw);
  if (value.kind !== 'kxdex-ticket-presentation' || value.version !== 2
      || typeof value.presentationId !== 'string' || typeof value.challenge !== 'string'
      || typeof value.expiresAt !== 'number') {
    throw new Error('无法识别的动态票码');
  }
  if (value.expiresAt <= Date.now()) throw new Error('动态票码已过期，请持票人重新生成');
  return value as unknown as TicketPresentationPayload;
};

export const createOperationId = () => {
  if (typeof crypto.randomUUID !== 'function') {
    throw new Error('当前浏览器不支持安全操作标识，请升级浏览器');
  }
  return crypto.randomUUID();
};
